import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, articles } from '@/mocks/siteData';

function renderMarkdown(text: string): string {
  let html = text;
  html = html.replace(/### (.+)/g, '<h3 class="text-lg font-heading font-semibold text-foreground-900 mt-8 mb-3">$1</h3>');
  html = html.replace(/## (.+)/g, '<h2 class="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mt-10 mb-4">$1</h2>');
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong class="text-foreground-900">$1</strong>');
  const paragraphs = html.split('\n\n');
  return paragraphs.map((p) => {
    const trimmed = p.trim();
    if (!trimmed) return '';
    if (trimmed.startsWith('<h2') || trimmed.startsWith('<h3')) return trimmed;
    return `<p class="text-sm md:text-base text-foreground-700 leading-relaxed mb-3">${trimmed.replace(/\n/g, '<br />')}</p>`;
  }).join('\n');
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="pt-20">
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-4">
            <i className="ri-question-line text-2xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-heading font-semibold text-foreground-900 mb-2">文章未找到</h1>
          <p className="text-sm text-foreground-600 mb-6">你访问的文章不存在或已被移除。</p>
          <Link to="/" className="text-sm text-accent-600 hover:text-accent-700 font-medium whitespace-nowrap cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const cat = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles.filter((a) => a.category === article.category && a.slug !== article.slug).slice(0, 3);
  const contentHtml = renderMarkdown(article.content);

  return (
    <div className="pt-16">
      <div className="bg-background-100 border-b border-background-200">
        <div className="max-w-4xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '__CATEGORY__', href: `/category/${article.category}` },
              { label: article.title },
            ]}
            categorySlug={article.category}
          />
        </div>
      </div>

      <article className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <header className="mb-8">
          {cat && (
            <Link
              to={`/category/${cat.slug}`}
              className="inline-block text-xs text-accent-600 bg-accent-100 px-3 py-1 rounded-full mb-4 whitespace-nowrap hover:bg-accent-200 transition-colors cursor-pointer"
            >
              {cat.name}
            </Link>
          )}
          <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-xs md:text-sm text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.readTime} 分钟阅读
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              发布于 {article.publishedAt}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-user-line"></i>
              {cat?.name || ''}
            </span>
          </div>
        </header>

        <div className="relative w-full h-56 md:h-80 rounded-lg overflow-hidden mb-8">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover object-top"
          />
        </div>

        <div
          className="article-content prose-custom"
          dangerouslySetInnerHTML={{ __html: contentHtml }}
        />

        <div className="mt-12 pt-8 border-t border-background-200">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs text-foreground-500 whitespace-nowrap">分享文章：</span>
            <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-accent-100 hover:text-accent-600 transition-colors cursor-pointer">
              <i className="ri-wechat-line text-sm"></i>
            </button>
            <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-accent-100 hover:text-accent-600 transition-colors cursor-pointer">
              <i className="ri-weibo-line text-sm"></i>
            </button>
            <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-accent-100 hover:text-accent-600 transition-colors cursor-pointer">
              <i className="ri-link text-sm"></i>
            </button>
          </div>
        </div>
      </article>

      {relatedArticles.length > 0 && (
        <section className="bg-background-100 py-12 md:py-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-6">
              相关文章
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.slug}
                  to={`/article/${ra.slug}`}
                  className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-accent-300 transition-all duration-200 cursor-pointer flex flex-col"
                >
                  <div className="relative w-full h-36 overflow-hidden">
                    <img
                      src={ra.imageUrl}
                      alt={ra.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-2 line-clamp-2 group-hover:text-accent-700 transition-colors">
                      {ra.title}
                    </h3>
                    <div className="flex items-center gap-2 text-xs text-foreground-500 mt-auto">
                      <span>{ra.readTime} 分钟</span>
                      <span>{ra.publishedAt}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}